import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Button, TextareaAutosize } from "@mui/material";
import React, { ReactElement, useState } from "react";

import { RenderingObjectKind } from "../../generated/gql/graphql";
import { classNames } from "../../utils/classes";
import { CanvasObject } from "../layout/editor/canvas-object";
import { ContentList } from "../layout/editor/content-list";

import { CanvasObjectText } from "./canvas-object-text";
import { TextAiTools } from "./text-ai-tools";

export type TextsPanelProps = {
  /** Callback function to handle adding a new text */
  onAddText: () => void;

  canvasObjects: CanvasObject[];
  objectChanged: (
    index: number,
    kind: RenderingObjectKind,
    changes: Partial<CanvasObject>,
  ) => void;
};

export function TextsPanel({
  canvasObjects,
  objectChanged,
}: TextsPanelProps): ReactElement {
  const [showTextsAI, setShowTextsAI] = useState<
    {
      index: number;
      isShowAI: boolean;
    }[]
  >();

  function handleTextContentChange(
    newContent: string,
    currentObject: CanvasObjectText,
    index: number,
  ): void {
    objectChanged(index, RenderingObjectKind.Text, {
      text: {
        ...currentObject.text,
        content: newContent,
      },
    });
  }

  function handleTextChange(index: number): void {
    const currentText = showTextsAI?.find((t) => t.index === index);

    if (!!currentText && currentText.isShowAI) {
      setShowTextsAI((prev) => prev?.filter((p) => p.index !== index));

      return;
    }

    setShowTextsAI((prev) => [
      ...(prev ?? []),
      {
        index,
        isShowAI: true,
      },
    ]);
  }

  return (
    <ContentList>
      {canvasObjects.map(
        (text, index) =>
          text.kind === "text" && (
            <div
              className="mb-[20px] border border-[#F0F0F0] rounded-md p-[10px] hover:shadow"
              key={text.id}
            >
              <div className="flex justify-between mb-[10px] items-center">
                <h2 className="font-bold text-[14px]">Text element</h2>
                <Button
                  className={classNames(
                    "font-bold rounded-md bg-white text-[#8958BD] hover:bg-[#B989ED] hover:text-white px-[6px] min-w-fit shadow",
                    !!showTextsAI?.find((t) => t.index === index)
                      ? "bg-[#B989ED] !text-white"
                      : "",
                  )}
                  variant="contained"
                  onClick={() => handleTextChange(index)}
                >
                  <AutoAwesomeIcon />
                </Button>
              </div>
              <TextareaAutosize
                className="p-[10px] mb-[10px] inline-block w-full border-1 border-[#A1A1A1] border rounded-md"
                value={text.text.content}
                onChange={(e) =>
                  handleTextContentChange(e.target.value, text, index)
                }
                onBlur={(e) =>
                  handleTextContentChange(e.target.value, text, index)
                }
              ></TextareaAutosize>
              {!!showTextsAI?.find((t) => t.index === index) && (
                <TextAiTools
                  textObject={text}
                  onTextChange={(updateText) =>
                    handleTextContentChange(updateText, text, index)
                  }
                />
              )}
            </div>
          ),
      )}
    </ContentList>
  );
}
